import React, { createContext, FC, useContext, useEffect } from "react";
import { ApolloError, useQuery } from "@apollo/client";
import queries from "../Shared/queries";
import { DPCompany, DPEmployer } from "Shared/types/PartnerConfig";
import {
  PartnerQueryConfigQuery,
  Provider,
  ProviderPermissions,
} from "generated/graphql";

const initialEmployerState = {
  id: "",
  firstName: "",
  lastName: "",
} as DPEmployer;

export interface OffCyclePaymentOptions {
  cycleDpaFeeCents?: number;
  cycleUserBankFeeCents?: number;
  cycleRequiresApproval: boolean;
  enableOffCycleDisbursements: boolean;
  enableBatchOffCycleDisbursements: boolean;
  maxAmountCents?: number;
}

export interface RewardsOptions {
  enableRewards: boolean;
  canSendRewards: boolean;
  maxMonthlyBudgetAmountCents: number;
}

export interface BalanceDistributorOptions {
  enabled: boolean;
}

const initialEnabledFeatures: string[] = [];
const initialAllowedDomainNames: string[] = [];
const initialPermissions: ProviderPermissions = {} as ProviderPermissions;
const initialCompanies = [{ id: "", payGroup: "" }] as DPCompany[];

export type InitialPartnerConfigContextState = {
  employer: DPEmployer;
  currentProvider?: Provider;
  providers?: Provider[];
  enabledFeatures: string[];
  permissions: ProviderPermissions;
  allowedDomainNames: string[];
  companies: DPCompany[];
  associatedLocationsCount: number | undefined;
  allowedDomains: string[];
  loading: boolean;
  error?: ApolloError;
  offCyclePaymentOptions: OffCyclePaymentOptions;
  rewardsOptions?: RewardsOptions;
  balanceDistributorOptions?: BalanceDistributorOptions;
  hasMultiplePaygroups: boolean;
};

export const initialPartnerConfigContextState: InitialPartnerConfigContextState = {
  employer: initialEmployerState,
  currentProvider: undefined,
  providers: [],
  enabledFeatures: initialEnabledFeatures,
  permissions: initialPermissions,
  allowedDomainNames: initialAllowedDomainNames,
  companies: initialCompanies,
  associatedLocationsCount: undefined,
  allowedDomains: [],
  loading: true,
  error: undefined,
  offCyclePaymentOptions: {
    cycleRequiresApproval: false,
    enableOffCycleDisbursements: false,
    enableBatchOffCycleDisbursements: false,
  },
  rewardsOptions: undefined,
  hasMultiplePaygroups: false,
};

const PartnerConfigContext = createContext(initialPartnerConfigContextState);

export const PartnerConfigProvider: FC = ({ children }) => {
  const [config, setConfig] = React.useState(initialPartnerConfigContextState);

  const { data, loading, error } = useQuery<PartnerQueryConfigQuery>(
    queries.PARTNER_CONFIG_QUERY
  );

  const configEmployer = (data: PartnerQueryConfigQuery) => {
    let employer = initialEmployerState;
    if (data && data.employer) {
      const newEmployer = {
        id: data.employer.id,
        firstName: data.employer.firstName,
        lastName: data.employer.lastName,
      } as DPEmployer;
      employer = newEmployer;
    }

    return employer;
  };

  const configCurrentProvider = (data: PartnerQueryConfigQuery) => {
    let provider;
    if (data && data.employer.currentProvider) {
      const newProvider = {
        id: data.employer.currentProvider.id,
        name: data.employer.currentProvider.name,
        icon: data.employer.currentProvider.icon,
        whiteLabelPartnerIdentifier:
          data.employer.currentProvider.whiteLabelPartnerIdentifier,
        pendingUserSmsAllowed:
          data.employer.currentProvider.pendingUserSmsAllowed,
      } as Provider;
      provider = newProvider;
    }
    return provider;
  };

  const configProviders = (data: PartnerQueryConfigQuery) => {
    let providers;
    if (data && data.employer.providers) {
      const newProviders = data.employer.providers.items.map(
        ({ icon, id, name }) => ({ icon, id, name })
      ) as Provider[];
      providers = newProviders;
    }
    return providers;
  };

  const configEnabledFeatures = (data: PartnerQueryConfigQuery) => {
    let enabledFeatures = initialEnabledFeatures;
    if (data.employer && data.employer.currentProvider) {
      enabledFeatures = data.employer.currentProvider.enabledFeatures.items.map(
        (item) => item.name
      );
    }

    return enabledFeatures;
  };
  const configPermissions = (data: PartnerQueryConfigQuery) => {
    let permissions = initialPermissions;
    if (data.employer && data.employer.currentProvider) {
      permissions = data.employer.currentProvider
        .permissions as ProviderPermissions;
    }

    return permissions;
  };
  const configCompanies = (data: PartnerQueryConfigQuery) => {
    let companies: DPCompany[] = initialCompanies;
    if (data && data.companies) {
      companies = data.companies.items;
    }
    return companies;
  };
  const configAssociatedLocations = (data: PartnerQueryConfigQuery) => {
    const associatedLocationsCount =
      data.employer?.associatedLocations?.totalCount;

    return associatedLocationsCount;
  };
  const configAllowedDomainNames = (data: PartnerQueryConfigQuery) => {
    let domainNames: string[] = initialAllowedDomainNames;
    if (data.employer && data.employer.currentProvider) {
      domainNames = data.employer.currentProvider.allowedDomainNames || [];
    }
    return domainNames;
  };
  const configOffCyclePaymentOptions = (data: PartnerQueryConfigQuery) => {
    return {
      cycleDpaFeeCents:
        data.employer.currentProvider.offCyclePaymentOptions.cycleDpaFeeCents,
      cycleUserBankFeeCents:
        data.employer.currentProvider.offCyclePaymentOptions
          .cycleUserBankFeeCents,
      cycleRequiresApproval:
        data.employer.currentProvider.offCyclePaymentOptions
          .cycleRequiresApproval,
      enableOffCycleDisbursements:
        data.employer.currentProvider.offCyclePaymentOptions
          .enableOffCycleDisbursements,
      enableBatchOffCycleDisbursements:
        data.employer.currentProvider.offCyclePaymentOptions
          .enableBatchOffCycleDisbursements,
      maxAmountCents:
        data.employer.currentProvider.offCyclePaymentOptions.maxAmountCents,
    };
  };
  const configRewardsOptions = (data: PartnerQueryConfigQuery) => {
    return {
      enableRewards: data.employer.currentProvider.rewardOptions.enableRewards,
      canSendRewards:
        data.employer.currentProvider.rewardOptions.canSendRewards,
      maxMonthlyBudgetAmountCents:
        data.employer.currentProvider.rewardOptions.maxMonthlyBudgetAmountCents,
    };
  };

  const configBalanceDistributor = (
    data: PartnerQueryConfigQuery
  ): BalanceDistributorOptions => {
    return {
      enabled: data.employer.currentProvider.payrollExtenderEnabled,
    };
  };

  const configHasMultiplePaygroups = (data: PartnerQueryConfigQuery) => {
    return data.companies.totalCount > 1;
  };

  useEffect(() => {
    if (!loading && data && !error) {
      const employer = configEmployer(data);
      const currentProvider = configCurrentProvider(data);
      const providers = configProviders(data);
      const enabledFeatures = configEnabledFeatures(data);
      const allowedDomainNames = configAllowedDomainNames(data);
      const permissions = configPermissions(data);
      const companies = configCompanies(data);
      const allowedDomains = configAllowedDomainNames(data);
      const associatedLocationsCount = configAssociatedLocations(data);
      const offCyclePaymentOptions = configOffCyclePaymentOptions(data);
      const rewardsOptions = configRewardsOptions(data);
      const hasMultiplePaygroups = configHasMultiplePaygroups(data);
      const balanceDistributorOptions = configBalanceDistributor(data);

      setConfig({
        employer,
        currentProvider,
        providers,
        enabledFeatures,
        allowedDomainNames,
        permissions,
        companies,
        associatedLocationsCount,
        allowedDomains,
        loading,
        offCyclePaymentOptions,
        rewardsOptions,
        hasMultiplePaygroups,
        balanceDistributorOptions,
      });
    } else if (!loading) {
      setConfig({
        ...config,
        loading: false,
        error,
      });
    }
    // eslint-disable-next-line
  }, [data, loading, error]);

  return (
    <PartnerConfigContext.Provider value={{ ...config }}>
      {children}
    </PartnerConfigContext.Provider>
  );
};

export const usePartnerConfig = () => useContext(PartnerConfigContext);
